/*
 * ct-eg-rules.js
 *
 * Eén leesvenster op de regels van EG Auto Add to Cart (vendor 506). De app
 * zet al zijn ACTIEVE regels als JSON in <meta id="easygift-rules">, maar
 * pas NA het laden van de pagina (het app-embed rendert de meta via script).
 * Iedereen die de regels nodig heeft (ct-gift-picker.js, ct-gift-ladder.js,
 * ct-gift-terms.js) leest ze via dit bestand, zodat er maar één plek is die
 * het ongedocumenteerde formaat van de app kent. Verandert de app zijn JSON,
 * dan breekt alles hier tegelijk en zichtbaar, in plaats van op drie plekken
 * half.
 *
 * Formaat (afgelezen 24-9-2026, app-versie 3.0):
 *   rule.active, rule.name, rule.schedule{enabled,starts,ends}
 *   rule.trigger.type: 'collection' | 'cartValue' | ...
 *   rule.trigger.collections[].gid, .condition ('value' | 'quantity' | null),
 *     .conditionMin (EURO bij 'value', STUKS bij 'quantity')
 *     — 'quantity' is een HYPOTHESE (5-10): de toilettas-regel (3 stuks) is
 *     nog niet in de meta nagekeken. Zie de checklist van de overdracht.
 *   rule.trigger.minCartValue (EURO) bij type 'cartValue'
 *   rule.action.type: 'addAutomatically' | 'offerToCustomer'
 *   rule.action.products[].variantId            (addAutomatically)
 *   rule.action.products[].{id,title,handle,variants[].id}  (offerToCustomer)
 *   rule.action.popupOptions.rewardQuantity
 *   Voorraad staat NIET in de meta; zie giftInfo().
 *
 * GIFTDATA (verhuisd uit ct-gift-ladder.js, 5-10): naam, foto, waarde,
 * vendor en beschikbaarheid van een gift. De app kent bij automatische
 * regels alleen variant-id's. Eén keer per sessie per gift:
 * HEAD /variants/<id> (de redirect naar de productpagina geeft de handle)
 * en dan /products/<handle>.js. Keuzeregels leveren de handle direct.
 * Gecachet in sessionStorage; ladder en actievoorwaarden-pagina delen die
 * cache. Zie giftInfo().
 *
 * HERVALIDEREN (David, 13-10): de sessie-cache maakt de ladder traag op
 * voorraadwijzigingen (een gift die uitverkoopt blijft tot het tabblad
 * dichtgaat staan). Daarom refresh(keys, cb): haalt voor bekende gifts
 * alleen /products/<handle>.js opnieuw op (de handle zit al in de cache,
 * dus zonder de HEAD-redirect), slaat de verse stand op en meldt of er
 * iets veranderd is. Throttle: per gift hoogstens één keer per
 * REFRESH_MIN_MS, via checkedAt in de cache zelf, zodat open/dicht/open
 * niet steeds fetcht. De ladder roept dit aan bij het openen van de
 * drawer: precies het moment waarop de klant ernaar kijkt.
 *
 * `available` (13-10): de ladder verbergt treden waarvan geen gift meer
 * leverbaar is. Daarom is het onderscheid tussen 'uitverkocht' en
 * 'onbekend' belangrijk: variant.available === false uit Shopify is
 * uitverkocht; een mislukte fetch is onbekend en wordt als leverbaar
 * opgeslagen (zie load()), anders zou een netwerkfout een trede laten
 * verdwijnen. Een 404 (product verwijderd of niet gepubliceerd) telt wél
 * als niet leverbaar: de app kan dat product ook niet toevoegen.
 *
 * Bewust geen ES module: wordt als <script defer> geladen vóór de picker en
 * de ladder (snippets/preload-js.liquid), zodat window.ctEasyGift er is.
 */
(function () {
  'use strict';

  var META_ID = 'easygift-rules';
  var CACHE_PREFIX = 'ct-gift-info:';
  var REFRESH_MIN_MS = 60000;
  var cacheRaw = null;
  var cacheRules = null;
  var waiters = [];
  var observer = null;

  /* ------------------------------------------------------------------ */
  /* Regels lezen                                                        */
  /* ------------------------------------------------------------------ */

  function read() {
    var meta = document.getElementById(META_ID);
    if (!meta) return null;
    var raw = meta.getAttribute('content') || meta.textContent || '';
    if (!raw) return null;
    if (raw === cacheRaw) return cacheRules;
    try {
      var rules = JSON.parse(raw);
      if (typeof rules === 'string') rules = JSON.parse(rules);
      if (!Array.isArray(rules)) return null;
      cacheRaw = raw;
      cacheRules = rules;
      return rules;
    } catch (e) {
      return null;
    }
  }

  function flush() {
    var rules = read();
    if (!rules) return false;
    var list = waiters;
    waiters = [];
    list.forEach(function (cb) {
      try { cb(rules); } catch (e) { /* een luisteraar mag de rest niet blokkeren */ }
    });
    if (observer) { observer.disconnect(); observer = null; }
    return true;
  }

  /* Roept cb(rules) aan zodra de meta er is; direct als hij er al staat.
     De observer kijkt naar de hele boom omdat niet vastligt waar de app de
     meta aanhangt; hij wordt losgekoppeld zodra de regels gelezen zijn. */
  function ready(cb) {
    var rules = read();
    if (rules) { cb(rules); return; }
    waiters.push(cb);
    if (!observer && window.MutationObserver) {
      observer = new MutationObserver(function () { flush(); });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  function isActive(rule) {
    if (!rule || rule.active === false || rule.testMode === true) return false;
    var s = rule.schedule;
    if (s && s.enabled) {
      var now = Date.now();
      if (s.starts && new Date(s.starts).getTime() > now) return false;
      if (s.ends && new Date(s.ends).getTime() < now) return false;
    }
    return true;
  }

  /* De keuzeregel (offerToCustomer) waarvan de producttitels precies de
     titels uit de popup zijn. Gebruikt door de picker. */
  function ruleForTitles(names) {
    var rules = read();
    if (!rules || !names || !names.length) return null;
    var best = null;
    rules.forEach(function (rule) {
      var a = rule && rule.action;
      if (!a || a.type !== 'offerToCustomer' || !Array.isArray(a.products)) return;
      var titles = a.products.map(function (p) { return (p.title || '').trim(); });
      var hits = names.filter(function (n) { return titles.indexOf(n) !== -1; }).length;
      if (hits && hits === names.length) best = rule;
    });
    return best;
  }

  /* Genormaliseerde treden. Alleen regels die collectie- of cartValue-
     gebonden zijn. Per trede:
       thresholdType 'value'    — bedragdrempel, threshold in CENTEN
                                  (0 = elk product uit de collectie)
       thresholdType 'quantity' — aantal-drempel, threshold in STUKS
       schedule {starts, ends}  — ISO-strings of null (alleen als enabled)
     De ladder gebruikt alleen 'value' (hij telt geen stuks); de
     actievoorwaarden-pagina toont beide.
     Regels waarvan de naam met TEST begint worden overgeslagen: die staan
     winkelbreed aan zodra ze in de meta zitten, ook op live. */
  function tiers() {
    var rules = read();
    if (!rules) return [];
    var out = [];
    rules.forEach(function (rule) {
      if (!isActive(rule)) return;
      if (/^test\b/i.test(rule.name || '')) return;
      var t = rule.trigger || {};
      var a = rule.action || {};
      var threshold = null;
      var thresholdType = 'value';
      var collections = [];
      if (t.type === 'collection') {
        collections = (t.collections || []).map(function (c) {
          return String((c && c.gid) || '').split('/').pop();
        }).filter(Boolean);
        if (!collections.length) return;
        if (t.condition === 'value' && t.conditionMin != null) {
          threshold = Math.round(Number(t.conditionMin) * 100);
        } else if (t.condition === 'quantity' && t.conditionMin != null) {
          thresholdType = 'quantity';
          threshold = parseInt(t.conditionMin, 10);
        } else if (t.condition == null && t.conditionMin == null) {
          threshold = 0;
        } else {
          return;
        }
      } else if (t.type === 'cartValue') {
        if (t.minCartValue == null) return;
        threshold = Math.round(Number(t.minCartValue) * 100);
      } else {
        return;
      }
      if (isNaN(threshold)) return;

      var gifts = [];
      var choice = null;
      if (a.type === 'offerToCustomer') {
        choice = {
          count: Math.max(1, parseInt(a.popupOptions && a.popupOptions.rewardQuantity, 10) || 1),
          products: (a.products || []).map(function (p) {
            return { id: p.id ? String(p.id) : null, title: p.title || '', handle: p.handle || '' };
          })
        };
      } else if (a.type === 'addAutomatically') {
        gifts = (a.products || []).map(function (p) {
          return p.variantId ? String(p.variantId) : null;
        }).filter(Boolean);
        if (!gifts.length) return;
      } else {
        return;
      }

      var s = rule.schedule;
      var schedule = (s && s.enabled) ? { starts: s.starts || null, ends: s.ends || null } : null;

      out.push({
        id: rule._id || rule.name,
        name: rule.name || '',
        groupKey: collections.length ? collections.slice().sort().join(',') : 'cart',
        collections: collections,
        threshold: threshold,
        thresholdType: thresholdType,
        schedule: schedule,
        gifts: gifts,
        choice: choice
      });
    });
    return out;
  }

  /* ------------------------------------------------------------------ */
  /* Giftdata (naam, foto, waarde, vendor, beschikbaarheid)              */
  /* key = 'v:<variantId>' of 'h:<handle>'                                */
  /* ------------------------------------------------------------------ */

  var info = {};
  var loading = {};
  var rootPath = null;

  /* Locale-prefix voor /variants en /products: Shopify.routes.root is het
     standaard theme-global; de ladder zet hem expliciet via setRoot
     (routes.root_url uit Liquid). */
  function root() {
    if (rootPath) return rootPath;
    var r = '/';
    try {
      if (window.Shopify && Shopify.routes && Shopify.routes.root) r = Shopify.routes.root;
    } catch (e) { /* genegeerd */ }
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function setRoot(path) {
    if (!path) return;
    rootPath = path.charAt(path.length - 1) === '/' ? path : path + '/';
  }

  function cached(key) {
    if (info[key]) return info[key];
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (raw) {
        var parsed = JSON.parse(raw);
        /* Oudere entries (zonder prijs of beschikbaarheid) negeren, dan
           wordt de gift ververst. */
        if (parsed && typeof parsed.price === 'number' && typeof parsed.available === 'boolean') {
          info[key] = parsed;
          return parsed;
        }
      }
    } catch (e) { /* storage niet beschikbaar */ }
    return null;
  }

  function store(key, data) {
    data.checkedAt = Date.now();
    info[key] = data;
    try { sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(data)); } catch (e) { /* genegeerd */ }
    var cbs = loading[key] || [];
    delete loading[key];
    cbs.forEach(function (cb) {
      try { cb(data); } catch (e) { /* een luisteraar mag de rest niet blokkeren */ }
    });
  }

  function empty() {
    return { title: '', image: '', price: 0, vendor: '', handle: '', available: false };
  }

  /* Onbekend (fetch mislukt): geen gegevens, maar wél leverbaar, zodat de
     ladder de trede laat staan. Zie de kop. */
  function unknown() {
    var out = empty();
    out.available = true;
    return out;
  }

  function productJson(handle) {
    return fetch(root() + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; });
  }

  function fromProduct(product, vid) {
    var out = empty();
    if (!product) return out;
    out.title = product.title || '';
    out.vendor = product.vendor || '';
    out.handle = product.handle || '';
    var variant = null;
    if (vid) {
      variant = (product.variants || []).filter(function (v) { return String(v.id) === String(vid); })[0];
    }
    if (!variant) variant = (product.variants || [])[0] || null;
    var src = (variant && variant.featured_image && variant.featured_image.src) ||
      product.featured_image || (product.images && product.images[0]) || '';
    if (src) out.image = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=320';
    if (variant) {
      /* Waarde = de hoogste van was- en verkoopprijs: dat is wat de klant
         cadeau krijgt. */
      out.price = Math.max(variant.compare_at_price || 0, variant.price || 0);
      out.available = variant.available === true;
    } else {
      out.available = product.available === true;
    }
    return out;
  }

  function load(key) {
    var kind = key.charAt(0);
    var id = key.slice(2);
    var p;
    if (kind === 'h') {
      p = productJson(id).then(function (product) { return fromProduct(product, null); });
    } else {
      p = fetch(root() + 'variants/' + encodeURIComponent(id), { method: 'HEAD', redirect: 'follow', credentials: 'same-origin' })
        .then(function (r) {
          var m = (r.url || '').match(/\/products\/([^/?#]+)/);
          if (!m) throw new Error('no handle');
          return productJson(m[1]);
        })
        .then(function (product) { return fromProduct(product, id); });
    }
    p.then(function (data) {
      store(key, data);
    }).catch(function () {
      /* Niets op te halen (netwerk, geblokkeerd): icoon en algemene tekst
         blijven staan, de gift telt als leverbaar. Niet opnieuw proberen
         in deze sessie. */
      store(key, unknown());
    });
  }

  /* giftInfo(key, cb): geeft de gegevens direct terug als ze bekend zijn
     (uit geheugen of sessionStorage). Zo niet: null, en cb(info) zodra ze
     binnen zijn. Meerdere aanroepers voor dezelfde key delen één fetch. */
  function giftInfo(key, cb) {
    var hit = cached(key);
    if (hit) return hit;
    if (loading[key]) {
      if (cb) loading[key].push(cb);
      return null;
    }
    loading[key] = cb ? [cb] : [];
    load(key);
    return null;
  }

  /* refresh(keys, cb): voorraad en prijs van bekende gifts opnieuw ophalen
     (zie de kop). Onbekende keys (nog niet in de cache) worden
     overgeslagen: die laadt giftInfo() zelf. Een gift waarvan de handle
     ontbreekt (eerdere fetch mislukt) wordt opnieuw via giftInfo geladen.
     cb(changed) na afloop, één keer, ook als er niets te doen was. Een
     mislukte hercheck laat de oude stand staan. */
  function refresh(keys, cb) {
    var now = Date.now();
    var pending = 0;
    var changed = false;
    var done = false;

    function finish() {
      if (done) return;
      done = true;
      if (cb) { try { cb(changed); } catch (e) { /* genegeerd */ } }
    }

    (keys || []).forEach(function (key) {
      var cur = cached(key);
      if (!cur) return;
      if (cur.checkedAt && now - cur.checkedAt < REFRESH_MIN_MS) return;
      if (loading[key]) return;

      if (!cur.handle) {
        /* Geen handle bekend: volledige herlaadroute, telt als wijziging
           als er daarna wél gegevens zijn. */
        delete info[key];
        try { sessionStorage.removeItem(CACHE_PREFIX + key); } catch (e) { /* genegeerd */ }
        pending++;
        giftInfo(key, function (data) {
          if (data && data.title) changed = true;
          if (--pending === 0) finish();
        });
        return;
      }

      var vid = key.charAt(0) === 'v' ? key.slice(2) : null;
      pending++;
      loading[key] = [];
      productJson(cur.handle).then(function (product) {
        if (!product) {
          /* 404: product weg of gedepubliceerd, dus niet leverbaar. */
          var gone = empty();
          gone.handle = cur.handle;
          if (cur.available) changed = true;
          store(key, gone);
          return;
        }
        var fresh = fromProduct(product, vid);
        if (fresh.available !== cur.available || fresh.price !== cur.price) changed = true;
        store(key, fresh);
      }).catch(function () {
        /* Hercheck mislukt: oude stand laten staan, wel de stempel
           vernieuwen zodat we niet elke opening opnieuw proberen. */
        delete loading[key];
        store(key, cur);
      }).then(function () {
        if (--pending === 0) finish();
      });
    });

    if (pending === 0) finish();
  }

  window.ctEasyGift = {
    read: read,
    ready: ready,
    isActive: isActive,
    ruleForTitles: ruleForTitles,
    tiers: tiers,
    giftInfo: giftInfo,
    refresh: refresh,
    setRoot: setRoot
  };
})();
